/**
 * token별 Blob IndexedDB 캐시 (LRU)
 * - token을 키로 blob 저장
 * - 용량 초과 시 LRU로 오래된 항목 제거
 * - 저장 위치: IndexedDB → moving-visit-admin-token-blob-cache
 */

const DB_NAME = 'moving-visit-admin-token-blob-cache';
const DB_VERSION = 1;
const STORE_BLOBS = 'blobs';
const STORE_META = 'meta';
const META_KEY = 'cache-meta';
const MAX_CACHE_BYTES = 100 * 1024 * 1024; // 100MB

interface CacheEntry {
  token: string;
  size: number;
  lastAccess: number;
}

interface CacheMeta {
  entries: CacheEntry[];
  totalSize: number;
}

function openDB(): Promise<IDBDatabase> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window undefined'));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'token' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
  });
}

async function loadMeta(db: IDBDatabase): Promise<CacheMeta> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readonly');
    const req = tx.objectStore(STORE_META).get(META_KEY);
    req.onsuccess = () => {
      const parsed = req.result as CacheMeta | undefined;
      resolve(
        parsed && Array.isArray(parsed.entries)
          ? {
              entries: parsed.entries,
              totalSize: typeof parsed.totalSize === 'number' ? parsed.totalSize : 0,
            }
          : { entries: [], totalSize: 0 }
      );
    };
    req.onerror = () => reject(req.error);
  });
}

async function saveMeta(db: IDBDatabase, meta: CacheMeta): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readwrite');
    const req = tx.objectStore(STORE_META).put(meta, META_KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function evictUntilUnderLimit(db: IDBDatabase, meta: CacheMeta): Promise<CacheMeta> {
  if (meta.totalSize <= MAX_CACHE_BYTES) return meta;

  const sorted = [...meta.entries].sort((a, b) => a.lastAccess - b.lastAccess);
  let totalSize = meta.totalSize;
  const evictedTokens = new Set<string>();

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_BLOBS, 'readwrite');
    const store = tx.objectStore(STORE_BLOBS);
    for (const entry of sorted) {
      if (totalSize <= MAX_CACHE_BYTES) break;
      store.delete(entry.token);
      totalSize -= entry.size;
      evictedTokens.add(entry.token);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  return {
    entries: meta.entries.filter((e) => !evictedTokens.has(e.token)),
    totalSize,
  };
}

/**
 * token에 해당하는 Blob 조회 (캐시 hit 시 lastAccess 갱신)
 */
export async function getTokenBlobFromCache(token: string): Promise<Blob | undefined> {
  if (typeof window === 'undefined') return undefined;

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_BLOBS, 'readonly');
      const req = tx.objectStore(STORE_BLOBS).get(token);
      req.onsuccess = async () => {
        const row = req.result as { token: string; blob: Blob } | undefined;
        if (!row?.blob) {
          db.close();
          resolve(undefined);
          return;
        }
        const meta = await loadMeta(db);
        const entry = meta.entries.find((e) => e.token === token);
        if (entry) {
          entry.lastAccess = Date.now();
          await saveMeta(db, meta);
        }
        db.close();
        resolve(row.blob);
      };
      req.onerror = () => {
        db.close();
        reject(req.error);
      };
    });
  } catch {
    return undefined;
  }
}

/**
 * token별 Blob을 캐시에 저장 (LRU eviction 적용)
 */
export async function setTokenBlobToCache(token: string, blob: Blob): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const db = await openDB();
    const now = Date.now();
    const size = blob.size;
    const meta = await loadMeta(db);

    const existing = meta.entries.find((e) => e.token === token);
    if (existing) {
      meta.totalSize -= existing.size;
      meta.entries = meta.entries.filter((e) => e.token !== token);
    }

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_BLOBS, 'readwrite');
      tx.objectStore(STORE_BLOBS).put({ token, blob });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    meta.entries.push({ token, size, lastAccess: now });
    meta.totalSize += size;

    const evicted = await evictUntilUnderLimit(db, meta);
    await saveMeta(db, evicted);
    db.close();
  } catch {
    /* ignore */
  }
}
